<template>
  <div class="section-wrapper">
    <section class="contact-section" id="contact" aria-label="Contact section">
      <div class="background-elements">
        <div class="grid-overlay"></div>
        <div class="floating-elements" aria-hidden="true">
          <div class="tech-circle"></div>
          <div class="tech-square"></div>
        </div>
      </div>

      <div class="content-container">
        <div class="contact-content">
          <div class="brand-tag slide-in" data-delay="100">
            <div class="tag-dot"></div>
            <span>Get in Touch</span>
          </div>

          <h2 class="main-heading">
            <div class="heading-line fade-in" data-delay="300">
              Ready to Start Your
            </div>
            <div class="heading-highlight fade-in" data-delay="500">
              Digital Journey
              <svg
                class="heading-accent"
                viewBox="0 0 100 100"
                width="80"
                height="80"
              >
                <circle cx="50" cy="50" r="40" class="accent-circle" />
              </svg>
            </div>
          </h2>

          <div class="contact-info fade-in" data-delay="700">
            <p class="contact-text">
              For collaboration inquiries, please contact us:
            </p>

            <div class="contact-details">
              <a href="tel:+14244734444" class="contact-item phone-item">
                <div class="icon-circle">
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path
                      d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                      stroke="currentColor"
                      fill="none"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <span class="contact-value">(424) 473-4444</span>
              </a>

              <a
                href="mailto:s.michael@vision-lab.studio"
                class="contact-item email-item"
              >
                <div class="icon-circle">
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path
                      d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                      stroke="currentColor"
                      fill="none"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <polyline
                      points="22,6 12,13 2,6"
                      stroke="currentColor"
                      fill="none"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <span class="contact-value">s.michael@vision-lab.studio</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { onMounted } from "vue";

export default {
  name: "ContactSection",
  setup() {
    onMounted(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const delay = entry.target.dataset.delay || 0;
              setTimeout(() => {
                entry.target.classList.add("visible");
              }, delay);
            }
          });
        },
        { threshold: 0.1 }
      );

      document.querySelectorAll(".fade-in, .slide-in").forEach((el) => {
        observer.observe(el);
      });

      return () => observer.disconnect();
    });

    return {};
  },
};
</script>

<style lang="scss" scoped>
.section-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #000000;
  position: relative;
  overflow: hidden;
  padding: 2rem 0;
}

.contact-section {
  --primary-color: #6366f1;
  --accent-color: #818cf8;
  --text-primary: #ffffff;
  --text-secondary: #e2e8f0;

  width: 100%;
  padding: 4rem 0;
}

.background-elements {
  position: fixed;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.gradient-sphere {
  position: absolute;
  top: -20%;
  right: -10%;
  width: 80vh;
  height: 80vh;
  background: radial-gradient(
    circle,
    rgba(99, 102, 241, 0.2) 0%,
    rgba(99, 102, 241, 0.1) 40%,
    transparent 70%
  );
  border-radius: 50%;
}

.grid-overlay {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(#3d73ff08 1px, transparent 1px),
    linear-gradient(90deg, #3d73ff08 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: radial-gradient(circle at 50% 50%, black, transparent 80%);
}

.content-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}

.contact-content {
  max-width: 800px;
  margin: 0 auto;
}

.brand-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 100px;
  margin-bottom: 2rem;

  .tag-dot {
    width: 8px;
    height: 8px;
    background: var(--accent-color);
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  span {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-primary);
    letter-spacing: 0.05em;
  }
}

.main-heading {
  margin-bottom: 3rem;

  .heading-line {
    font-size: clamp(1.5rem, 3vw, 2rem);
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
  }

  .heading-highlight {
    position: relative;
    font-size: clamp(2.5rem, 5vw, 4rem);
    font-weight: 700;
    color: var(--text-primary);
    line-height: 1.1;

    .heading-accent {
      position: absolute;
      top: -20px;
      right: -40px;
      z-index: -1;

      .accent-circle {
        fill: none;
        stroke: var(--accent-color);
        stroke-width: 2;
        stroke-dasharray: 251;
        stroke-dashoffset: 251;
        animation: drawCircle 2s ease forwards;
      }
    }
  }
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 2rem;

  .contact-text {
    font-size: 1.125rem;
    color: var(--text-secondary);
    line-height: 1.6;
  }

  .contact-details {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .contact-item {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    padding: 1.5rem 2rem;
    background: rgba(99, 102, 241, 0.1);
    border: 1px solid rgba(99, 102, 241, 0.2);
    border-radius: 12px;
    text-decoration: none;
    color: var(--text-primary);
    transition: all 0.3s ease;

    &:hover {
      background: rgba(99, 102, 241, 0.15);
      border-color: var(--primary-color);
      transform: translateX(8px);

      .icon-circle {
        background: var(--primary-color);
        border-color: var(--primary-color);
      }
    }

    .icon-circle {
      width: 48px;
      height: 48px;
      min-width: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(99, 102, 241, 0.2);
      border: 1px solid rgba(99, 102, 241, 0.3);
      border-radius: 50%;
      transition: all 0.3s ease;

      svg {
        color: var(--text-primary);
      }
    }

    .contact-value {
      font-size: 1.125rem;
      font-weight: 500;
      word-break: break-word;
    }
  }
}

.fade-in {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-in {
  opacity: 0;
  transform: translateX(-20px);
  transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);

  &.visible {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes drawCircle {
  to {
    stroke-dashoffset: 0;
  }
}

@media (max-width: 768px) {
  .contact-section {
    padding: 2rem 0;
  }

  .section-wrapper {
    padding: 1rem 0;
  }

  .contact-info {
    .contact-item {
      padding: 1.25rem 1.5rem;
      gap: 1rem;

      .icon-circle {
        width: 40px;
        height: 40px;
        min-width: 40px;

        svg {
          width: 18px;
          height: 18px;
        }
      }

      .contact-value {
        font-size: 1rem;
      }

      &:hover {
        transform: translateX(4px);
      }
    }
  }

  .main-heading .heading-highlight .heading-accent {
    width: 60px;
    height: 60px;
    right: -30px;
  }
}
</style>
